function isYmdFormat(datetime) {
  return typeof datetime === 'string' && /^\d{4}-\d{1,2}-\d{1,2}$/.test(datetime);
}

function parseOptions(options, datetime) {
  /**
   * This is necessary because of the way javascript handles non ISO date strings with y-m-d format
   * If you call new Date('2025-01-01') it is equivalent to new Date('2025-01-01T00:00:00Z') i.e. midnight UTC
   * This is important because if you then call .toLocaleString() on that date, it will return the date in the local timezone,
   * and will probably be the previous day and WRONG.
   * This code was added to ignore the given timezone if we are dealing with Y-m-d format and instead format in UTC,
   * so that the internal date timezone and the formatted timezone are always the same in this case.
   */
  if (isYmdFormat(datetime)) {
    return {...options, timeZone: 'UTC'};
  }
  return options;
}

function isISODateString(datetime) {
  // Check if input is actually a string
  if (typeof datetime !== 'string') {
    return false;
  }

  // This regex matches ISO 8601 format dates:
  // YYYY-MM-DDTHH:mm:ss.sssZ or YYYY-MM-DDTHH:mm:ss.sss±HH:mm
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

  if (!isoDatePattern.test(datetime)) {
    return false;
  }

  // Additional validation by trying to parse the date
  try {
    const date = new Date(datetime);
    return !Number.isNaN(date.getTime());
  } catch (e) {
    return false;
  }
}

/**
 * Formats date to a human-readable format
 * i.e. "Jan 1, 2024"
 * @param {string|Date} datetime - ISO date string or unix timestamp or Date()
 * @param {string} timeZone - the timezone string i.e. 'America/New_York', 'The IANA timezone identifier for the Pacific timezone in the United States is:America/Los_Angeles', etc.
 *                            In the template you can access it via the Django {{ TIME_ZONE }} constant
 * @param {boolean} year - whether to include the year - disallow shortening it
 * @param {string} month - The month format ('numeric', '2-digit', 'short', 'long')
 * @param {string} day - The day format ('numeric', '2-digit')
 */
export function humanDate(datetime, timeZone, {year = true, month = 'short', day = 'numeric'} = {}) {
  const date = new Date(datetime),
        options = {month, day, timeZone};
  if (year) {
    options.year = 'numeric';
  }
  return new Intl.DateTimeFormat('en-US', parseOptions(options, datetime)).format(date);
}

/**
 * Generate date range like Jan 1 - Feb 4, 2024
 * If no end date is provided, it will just return the start date
 * If the dates are in different years, both years will be shown
 * @param {string|Date} date1 ISO date string or unix timestamp or Date()
 * @param {string|Date} date2 (optional) ISO date string or unix timestamp or Date()
 * @param {string} timeZone - the timezone string i.e. 'America/New_York', 'America/Los_AngelesAmerica/Los_Angeles', etc.
 *                            In the template you can access it via the Django {{ TIME_ZONE }} constantAmerica/Los_Angeles
 */
export function humanDateRange(date1, date2, timeZone) {
  if (!date1) throw new Error('humanDateRange requires a start date');

  const startDate = new Date(date1),
        endDate = date2 ? new Date(date2) : startDate,

        options = {
          month: 'short', day: 'numeric', year: 'numeric', timeZone,
        },
        isStartYmd = isYmdFormat(date1),
        isEndYmd = isYmdFormat(date2),
        isSameFormat = isStartYmd === isEndYmd;

  if (!isSameFormat) throw new Error('Dates must be same format');
  const formatter = new Intl.DateTimeFormat('en-US', parseOptions(options, date1));

  return formatter
    .formatRange(startDate, endDate)
    .replace(/\u2009/g, ' ') // don't use weird non breaking space character
    .replace('–', '-'); // just use regular hyphen
}

/**
 * Formats a date to a longer human-readable format
 * "Monday, January 1, 2024"
 * @param {string|Date} datetime - ISO date string or unix timestamp or Date()
 * @param {string} timeZone - the timezone string i.e. 'America/New_York', 'America/Los_Angeles', etc.
 *                            In the template you can access it via the Django {{ TIME_ZONE }} constant
 */
export function humanDateLong(datetime, timeZone) {
  const date = new Date(datetime),
        options = {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone,
        };
  return new Intl.DateTimeFormat('en-US', parseOptions(options, datetime)).format(date);
}

/**
 * Formats a date to a shorter human-readable format
 * "1/1/24"
 * @param {string|Date} datetime - ISO date string or unix timestamp or Date()
 * @param {string} timeZone - the timezone string i.e. 'America/New_York', 'America/Los_Angeles', etc.
 *                            In the template you can access it via the Django {{ TIME_ZONE }} constant
 */
export function humanDateShort(datetime, timeZone) {
  const date = new Date(datetime),
        options = {
          year: 'numeric', month: 'numeric', day: 'numeric', timeZone,
        };
  return new Intl.DateTimeFormat('en-US', parseOptions(options, datetime)).format(date);
}

/**
 * Formats a datetime to a human-readable time format
 * example: "2:57 PM"
 * @param {string|Date} datetime - ISO date string or unix timestamp or Date()
 * @param {string} timeZone - the timezone string i.e. 'America/New_York', 'America/Los_Angeles', etc.
 *                            In the template you can access it via the Django {{ TIME_ZONE }} constant
 * @return {string}
 */
export function humanTime(datetime, timeZone) {
  const options = {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone,
  },
        date = new Date(datetime);

  // if datetime doesn't include time info, throw an error
  if (!isISODateString(datetime)) {
    throw new RangeError('Invalid format: humanTime requires a datetime with time info');
  }

  return new Intl.DateTimeFormat('en-US', options).format(date);
}
